import { ChangeDetectionStrategy, Component, computed, OnDestroy, signal } from '@angular/core';

interface Challenge {
  readonly category: string;
  readonly question: string;
  /** two focal concepts, like a real map with two hubs */
  readonly hubs: readonly [string, string];
  readonly primary: readonly string[];
  readonly secondary: readonly string[];
  readonly links: readonly (readonly [string, string])[];
}

const CHALLENGES: readonly Challenge[] = [
  {
    category: 'Packaging',
    question: 'How can we make cold-chain packaging greener without increasing cost?',
    hubs: ['Cold-chain packaging', 'Sustainable materials'],
    primary: [
      'Thermal performance',
      'Shelf life',
      'Reuse systems',
      'Cost per shipment',
      'Packaging weight',
      'Bio-based insulation',
      'Recyclability',
      'Food waste',
      'Product protection',
      'Return logistics',
      'Carbon footprint',
      'Compliance',
    ],
    secondary: [
      'Phase-change materials',
      'Vacuum panels',
      'Moisture resistance',
      'Mycelium foam',
      'Paper liners',
      'Cold rooms',
      'Last-mile delivery',
      'Retail requirements',
      'Supplier availability',
      'Temperature excursions',
      'Data loggers',
      'Pallet utilisation',
      'Consumer perception',
      'EPR fees',
      'Pilot trials',
      'Open questions',
    ],
    links: [
      ['Cold-chain packaging', 'Thermal performance'],
      ['Cold-chain packaging', 'Shelf life'],
      ['Cold-chain packaging', 'Cost per shipment'],
      ['Cold-chain packaging', 'Product protection'],
      ['Cold-chain packaging', 'Reuse systems'],
      ['Cold-chain packaging', 'Temperature excursions'],
      ['Sustainable materials', 'Bio-based insulation'],
      ['Sustainable materials', 'Recyclability'],
      ['Sustainable materials', 'Carbon footprint'],
      ['Sustainable materials', 'Packaging weight'],
      ['Sustainable materials', 'Mycelium foam'],
      ['Sustainable materials', 'Paper liners'],
      ['Bio-based insulation', 'Thermal performance'],
      ['Bio-based insulation', 'Moisture resistance'],
      ['Thermal performance', 'Phase-change materials'],
      ['Thermal performance', 'Vacuum panels'],
      ['Thermal performance', 'Temperature excursions'],
      ['Shelf life', 'Food waste'],
      ['Shelf life', 'Cold rooms'],
      ['Food waste', 'Last-mile delivery'],
      ['Reuse systems', 'Return logistics'],
      ['Reuse systems', 'Packaging weight'],
      ['Return logistics', 'Last-mile delivery'],
      ['Return logistics', 'Pallet utilisation'],
      ['Cost per shipment', 'Packaging weight'],
      ['Cost per shipment', 'Supplier availability'],
      ['Cost per shipment', 'Pallet utilisation'],
      ['Recyclability', 'EPR fees'],
      ['Recyclability', 'Consumer perception'],
      ['Compliance', 'Retail requirements'],
      ['Compliance', 'EPR fees'],
      ['Retail requirements', 'Shelf life'],
      ['Product protection', 'Moisture resistance'],
      ['Carbon footprint', 'Last-mile delivery'],
      ['Temperature excursions', 'Data loggers'],
      ['Data loggers', 'Pilot trials'],
      ['Pilot trials', 'Bio-based insulation'],
      ['Open questions', 'Reuse systems'],
      ['Open questions', 'Consumer perception'],
      ['Supplier availability', 'Mycelium foam'],
    ],
  },
  {
    category: 'Academia',
    question: 'How can we run larger studies without dramatically increasing cost?',
    hubs: ['Large-scale research', 'Remote data collection'],
    primary: [
      'Participant recruitment',
      'Automated analysis',
      'Study quality',
      'Sample diversity',
      'Research staff',
      'Participant burden',
      'Data validation',
      'Wearables',
      'Online experiments',
      'Dropout rates',
      'Data privacy',
      'Funding cycles',
    ],
    secondary: [
      'Sampling bias',
      'Participant incentives',
      'Standardised protocols',
      'Replication',
      'Ethics approval',
      'Open datasets',
      'Panel providers',
      'Consent flows',
      'Signal quality',
      'Passive sensing',
      'Pre-registration',
      'Statistical power',
      'Lab time',
      'Training costs',
      'Longitudinal design',
      'Open questions',
    ],
    links: [
      ['Large-scale research', 'Participant recruitment'],
      ['Large-scale research', 'Study quality'],
      ['Large-scale research', 'Research staff'],
      ['Large-scale research', 'Funding cycles'],
      ['Large-scale research', 'Statistical power'],
      ['Large-scale research', 'Sample diversity'],
      ['Remote data collection', 'Wearables'],
      ['Remote data collection', 'Online experiments'],
      ['Remote data collection', 'Data privacy'],
      ['Remote data collection', 'Participant burden'],
      ['Remote data collection', 'Passive sensing'],
      ['Remote data collection', 'Signal quality'],
      ['Participant recruitment', 'Panel providers'],
      ['Participant recruitment', 'Participant incentives'],
      ['Participant recruitment', 'Sample diversity'],
      ['Sample diversity', 'Sampling bias'],
      ['Participant burden', 'Dropout rates'],
      ['Dropout rates', 'Longitudinal design'],
      ['Automated analysis', 'Research staff'],
      ['Automated analysis', 'Data validation'],
      ['Automated analysis', 'Training costs'],
      ['Data validation', 'Replication'],
      ['Data validation', 'Signal quality'],
      ['Study quality', 'Standardised protocols'],
      ['Study quality', 'Statistical power'],
      ['Standardised protocols', 'Pre-registration'],
      ['Replication', 'Open datasets'],
      ['Data privacy', 'Consent flows'],
      ['Data privacy', 'Ethics approval'],
      ['Ethics approval', 'Consent flows'],
      ['Research staff', 'Lab time'],
      ['Research staff', 'Training costs'],
      ['Funding cycles', 'Participant incentives'],
      ['Wearables', 'Passive sensing'],
      ['Online experiments', 'Panel providers'],
      ['Online experiments', 'Sampling bias'],
      ['Open questions', 'Longitudinal design'],
      ['Open questions', 'Automated analysis'],
      ['Lab time', 'Study quality'],
      ['Open datasets', 'Sample diversity'],
    ],
  },
  {
    category: 'Business',
    question: 'How can we personalize products without increasing production complexity?',
    hubs: ['Product personalization', 'Manufacturing flexibility'],
    primary: [
      'Modular design',
      'Configure-to-order',
      'Customer preferences',
      'SKU complexity',
      'Unit cost',
      'Demand forecasting',
      'Digital configuration',
      'Common components',
      'Postponement',
      'Inventory',
      'Setup time',
      'Quality control',
    ],
    secondary: [
      'Additive manufacturing',
      'Product variants',
      'Supplier lead time',
      'Assembly lines',
      'Data integration',
      'Pricing tiers',
      'Return rates',
      'Order intake',
      'Warehouse space',
      'Line changeovers',
      'Service parts',
      'Platform architecture',
      'Customer segments',
      'Margin per variant',
      'Pilot factory',
      'Open questions',
    ],
    links: [
      ['Product personalization', 'Customer preferences'],
      ['Product personalization', 'Configure-to-order'],
      ['Product personalization', 'SKU complexity'],
      ['Product personalization', 'Digital configuration'],
      ['Product personalization', 'Pricing tiers'],
      ['Product personalization', 'Customer segments'],
      ['Manufacturing flexibility', 'Setup time'],
      ['Manufacturing flexibility', 'Modular design'],
      ['Manufacturing flexibility', 'Additive manufacturing'],
      ['Manufacturing flexibility', 'Assembly lines'],
      ['Manufacturing flexibility', 'Unit cost'],
      ['Manufacturing flexibility', 'Line changeovers'],
      ['Modular design', 'Common components'],
      ['Modular design', 'Platform architecture'],
      ['Common components', 'Service parts'],
      ['Configure-to-order', 'Order intake'],
      ['Configure-to-order', 'Digital configuration'],
      ['Digital configuration', 'Data integration'],
      ['Customer preferences', 'Demand forecasting'],
      ['Customer preferences', 'Customer segments'],
      ['Demand forecasting', 'Inventory'],
      ['SKU complexity', 'Inventory'],
      ['SKU complexity', 'Product variants'],
      ['SKU complexity', 'Margin per variant'],
      ['Inventory', 'Warehouse space'],
      ['Postponement', 'Inventory'],
      ['Postponement', 'Product variants'],
      ['Unit cost', 'Margin per variant'],
      ['Unit cost', 'Supplier lead time'],
      ['Setup time', 'Line changeovers'],
      ['Quality control', 'Product variants'],
      ['Quality control', 'Return rates'],
      ['Return rates', 'Customer preferences'],
      ['Supplier lead time', 'Common components'],
      ['Pilot factory', 'Additive manufacturing'],
      ['Pilot factory', 'Setup time'],
      ['Open questions', 'Postponement'],
      ['Open questions', 'Pricing tiers'],
      ['Data integration', 'Demand forecasting'],
      ['Platform architecture', 'SKU complexity'],
    ],
  },
  {
    category: 'Healthcare',
    question: 'How can hospitals reduce waiting times without adding staff or beds?',
    hubs: ['Patient flow', 'Diagnostic bottlenecks'],
    primary: [
      'Triage',
      'Scheduling',
      'Bed turnover',
      'Discharge planning',
      'Staff workload',
      'Demand peaks',
      'Care pathways',
      'Lab turnaround',
      'Imaging queues',
      'Length of stay',
      'Digital intake',
      'Remote follow-up',
    ],
    secondary: [
      'No-shows',
      'Handover delays',
      'Resource allocation',
      'Transport teams',
      'Cleaning cycles',
      'Referral quality',
      'Point-of-care tests',
      'Radiology staffing',
      'Protocol variation',
      'Emergency admissions',
      'Social care capacity',
      'Nurse rostering',
      'Patient communication',
      'Readmissions',
      'Capacity dashboards',
      'Open questions',
    ],
    links: [
      ['Patient flow', 'Triage'],
      ['Patient flow', 'Bed turnover'],
      ['Patient flow', 'Scheduling'],
      ['Patient flow', 'Demand peaks'],
      ['Patient flow', 'Care pathways'],
      ['Patient flow', 'Length of stay'],
      ['Diagnostic bottlenecks', 'Lab turnaround'],
      ['Diagnostic bottlenecks', 'Imaging queues'],
      ['Diagnostic bottlenecks', 'Point-of-care tests'],
      ['Diagnostic bottlenecks', 'Radiology staffing'],
      ['Diagnostic bottlenecks', 'Length of stay'],
      ['Diagnostic bottlenecks', 'Triage'],
      ['Triage', 'Digital intake'],
      ['Triage', 'Emergency admissions'],
      ['Scheduling', 'No-shows'],
      ['Scheduling', 'Patient communication'],
      ['Bed turnover', 'Cleaning cycles'],
      ['Bed turnover', 'Discharge planning'],
      ['Discharge planning', 'Social care capacity'],
      ['Discharge planning', 'Remote follow-up'],
      ['Remote follow-up', 'Readmissions'],
      ['Staff workload', 'Handover delays'],
      ['Staff workload', 'Nurse rostering'],
      ['Staff workload', 'Care pathways'],
      ['Demand peaks', 'Resource allocation'],
      ['Demand peaks', 'Emergency admissions'],
      ['Resource allocation', 'Capacity dashboards'],
      ['Care pathways', 'Protocol variation'],
      ['Care pathways', 'Referral quality'],
      ['Length of stay', 'Readmissions'],
      ['Imaging queues', 'Radiology staffing'],
      ['Lab turnaround', 'Point-of-care tests'],
      ['Digital intake', 'Referral quality'],
      ['Bed turnover', 'Transport teams'],
      ['Transport teams', 'Imaging queues'],
      ['Capacity dashboards', 'Scheduling'],
      ['Open questions', 'Protocol variation'],
      ['Open questions', 'Social care capacity'],
      ['Nurse rostering', 'Demand peaks'],
      ['Patient communication', 'No-shows'],
    ],
  },
];

const W = 1080;
const H = 700;
const CX = W / 2;
const CY = H / 2 + 10;
const ROTATE_MS = 8000;

type Kind = 'hub' | 'primary' | 'secondary';

interface LayoutNode {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly kind: Kind;
}

interface LayoutEdge {
  readonly a: number;
  readonly b: number;
  readonly strong: boolean;
}

interface Layout {
  readonly nodes: readonly LayoutNode[];
  readonly edges: readonly LayoutEdge[];
  readonly adjacency: readonly (readonly number[])[];
}

interface RenderedNode extends LayoutNode {
  readonly index: number;
  readonly radius: number;
  readonly labelSize: number;
  readonly lit: boolean;
  readonly focused: boolean;
}

interface RenderedEdge extends LayoutEdge {
  readonly path: string;
  readonly lit: boolean;
  readonly index: number;
}

/** deterministic pseudo-random */
function rng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

function buildLayout(c: Challenge, seed: number): Layout {
  const ids = [...c.hubs, ...c.primary, ...c.secondary];
  const index = new Map(ids.map((id, i) => [id, i]));
  const kinds: Kind[] = ids.map((_, i) =>
    i < 2 ? 'hub' : i < 2 + c.primary.length ? 'primary' : 'secondary',
  );

  const edgeSet = new Set<string>();
  const edges: { a: number; b: number; strong: boolean }[] = [];
  const push = (a: number, b: number) => {
    const k = a < b ? `${a}-${b}` : `${b}-${a}`;
    if (edgeSet.has(k) || a === b) return;
    edgeSet.add(k);
    edges.push({ a, b, strong: kinds[a] === 'hub' || kinds[b] === 'hub' });
  };

  push(0, 1);
  c.links.forEach(([a, b]) => {
    const ai = index.get(a);
    const bi = index.get(b);
    if (ai === undefined || bi === undefined) return;
    push(ai, bi);
  });
  ids.forEach((_, i) => {
    if (i < 2) return;
    if (!edges.some((e) => e.a === i || e.b === i)) push(i, 2 + (i % c.primary.length));
  });

  const adjacency: number[][] = ids.map(() => []);
  edges.forEach((e) => {
    adjacency[e.a].push(e.b);
    adjacency[e.b].push(e.a);
  });

  // seeded placement: hubs left/right of centre, others spread wide
  const rand = rng(seed * 7919 + 13);
  const pos = ids.map((_, i) => {
    if (i === 0) return { x: CX - 200, y: CY - 6 };
    if (i === 1) return { x: CX + 210, y: CY - 40 };
    const a = rand() * Math.PI * 2;
    const r = (kinds[i] === 'primary' ? 150 : 250) + rand() * 150;
    return { x: CX + Math.cos(a) * r * 1.35, y: CY + Math.sin(a) * r * 0.86 };
  });

  // force relaxation — dense but non-overlapping
  for (let step = 0; step < 420; step++) {
    const fx = pos.map(() => 0);
    const fy = pos.map(() => 0);
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        const dx = pos[i].x - pos[j].x;
        const dy = (pos[i].y - pos[j].y) * 1.6; // extra vertical spacing for labels
        const d2 = dx * dx + dy * dy || 1;
        const rep = 165000 / d2;
        const d = Math.sqrt(d2);
        fx[i] += (dx / d) * rep;
        fy[i] += (dy / d) * rep;
        fx[j] -= (dx / d) * rep;
        fy[j] -= (dy / d) * rep;
      }
    }
    edges.forEach((e) => {
      const dx = pos[e.b].x - pos[e.a].x;
      const dy = pos[e.b].y - pos[e.a].y;
      const d = Math.hypot(dx, dy) || 1;
      const rest = e.strong ? 170 : 145;
      const f = (d - rest) * 0.02;
      fx[e.a] += (dx / d) * f;
      fy[e.a] += (dy / d) * f;
      fx[e.b] -= (dx / d) * f;
      fy[e.b] -= (dy / d) * f;
    });
    for (let i = 0; i < pos.length; i++) {
      if (i < 2) continue;
      // mild pull to canvas centre so the cloud stays framed
      fx[i] += (CX - pos[i].x) * 0.004;
      fy[i] += (CY - pos[i].y) * 0.005;
      pos[i].x += Math.max(-8, Math.min(8, fx[i] * 0.5));
      pos[i].y += Math.max(-8, Math.min(8, fy[i] * 0.5));
      // keep clear of the floating question panel (top-left)
      if (pos[i].x < 440 && pos[i].y < 240) {
        pos[i].y += (240 - pos[i].y) * 0.18;
        pos[i].x += (440 - pos[i].x) * 0.05;
      }
      pos[i].x = Math.max(80, Math.min(W - 80, pos[i].x));
      pos[i].y = Math.max(70, Math.min(H - 50, pos[i].y));
    }
  }

  return {
    nodes: ids.map((id, i) => ({ id, x: pos[i].x, y: pos[i].y, kind: kinds[i] })),
    edges,
    adjacency,
  };
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-hero-map',
  templateUrl: './hero-map.html',
  styleUrl: './hero-map.css',
})
export class HeroMap implements OnDestroy {
  readonly challenges = CHALLENGES;
  readonly challengeIndex = signal(0);
  readonly challengeFade = signal(false);
  readonly hoveredNode = signal<number | null>(null);
  readonly selectedNode = signal(0);

  private readonly paused = signal(false);
  private rotationTimer: number | undefined;
  private resumeTimer: number | undefined;

  readonly challenge = computed(() => this.challenges[this.challengeIndex()]);
  readonly layout = computed(() => buildLayout(this.challenge(), this.challengeIndex() + 1));
  readonly focusedNode = computed(() => this.hoveredNode() ?? this.selectedNode());
  readonly neighbours = computed(() => new Set(this.layout().adjacency[this.focusedNode()] ?? []));
  readonly connectedFindings = computed(() => this.neighbours().size);
  readonly totalConnections = computed(() => this.layout().edges.length);
  readonly renderedNodes = computed<readonly RenderedNode[]>(() => {
    const layout = this.layout();
    const focus = this.focusedNode();
    const neighbours = this.neighbours();

    return layout.nodes.map((node, index) => {
      const hub = node.kind === 'hub';
      const secondary = node.kind === 'secondary';

      return {
        ...node,
        index,
        x: node.x,
        y: node.y,
        radius: hub ? 20 : secondary ? 9 : 12,
        labelSize: hub ? 17 : secondary ? 14 : 15,
        lit: index === focus || neighbours.has(index),
        focused: index === focus,
      };
    });
  });
  readonly renderedEdges = computed<readonly RenderedEdge[]>(() => {
    const nodes = this.renderedNodes();
    const focus = this.focusedNode();
    const neighbours = this.neighbours();

    return this.layout().edges.map((edge, index) => {
      const a = nodes[edge.a];
      const b = nodes[edge.b];
      const middleX = (a.x + b.x) / 2;
      const middleY = (a.y + b.y) / 2;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy) || 1;
      const curve = (14 * (index % 2 === 0 ? 1 : -1)) / length;

      return {
        ...edge,
        index,
        path: `M ${a.x} ${a.y} Q ${middleX - dy * curve} ${middleY + dx * curve} ${b.x} ${b.y}`,
        lit:
          (edge.a === focus && neighbours.has(edge.b)) ||
          (edge.b === focus && neighbours.has(edge.a)),
      };
    });
  });

  constructor() {
    this.scheduleRotation();
  }

  selectChallenge(index: number): void {
    this.challengeIndex.set(index);
    this.selectedNode.set(0);
    this.hoveredNode.set(null);
    this.triggerChallengeSwitch();
    this.holdRotation();
  }

  hoverNode(index: number): void {
    this.hoveredNode.set(index);
    this.holdRotation();
  }

  clearHoveredNode(): void {
    this.hoveredNode.set(null);
  }

  selectNode(index: number): void {
    this.selectedNode.set(index);
    this.holdRotation();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private holdRotation(): void {
    this.paused.set(true);
    this.clearTimers();
    this.resumeTimer = window.setTimeout(() => {
      this.paused.set(false);
      this.scheduleRotation();
    }, 9000);
  }

  private scheduleRotation(): void {
    this.rotationTimer = window.setTimeout(() => {
      this.challengeIndex.update((index) => (index + 1) % this.challenges.length);
      this.selectedNode.set(0);
      this.hoveredNode.set(null);
      this.triggerChallengeSwitch();
      this.scheduleRotation();
    }, ROTATE_MS);
  }

  private clearTimers(): void {
    if (this.rotationTimer !== undefined) {
      window.clearTimeout(this.rotationTimer);
    }
    if (this.resumeTimer !== undefined) {
      window.clearTimeout(this.resumeTimer);
    }
  }

  private triggerChallengeSwitch(): void {
    this.challengeFade.set(false);
    requestAnimationFrame(() => {
      this.challengeFade.set(true);
    });
  }
}
